import { DISPLAY_TIME_ZONE } from '../format.ts'

/**
 * 보정 기록 대화의 시각 입력 ↔ 계약 값 변환.
 *
 * 🔴 **`datetime-local` 은 브라우저 로컬 시간대로 읽힌다 — 우리 화면은 그렇지 않다.**
 *    이 리포는 표시 시간대를 `DISPLAY_TIME_ZONE`(현장 기준) 한 곳에 고정해 두었다
 *    (`format.ts` 머리말 — 「브라우저 로컬을 쓰면 같은 원장이 단말마다 다르게 보인다」).
 *    표시만 그 시간대로 하고 **입력은 브라우저 로컬로 읽으면**, 사용자가 화면에 보이는
 *    시각을 그대로 적어도 **다른 순간이 서버에 저장된다.** 읽는 값은 눈으로 틀린 걸 알지만
 *    **쓰는 값은 아무도 모른 채 원장에 봉인된다** — 그래서 양방향을 여기서 맞춘다.
 *
 * 🔴 **날짜 산술을 여기서 하지 않는다.** 「기본 기한 +30일」 같은 것을 만들지 마라 —
 *    계약이 「경과 판정 기준값은 미결(OQ-121)」이라 우리가 정할 값이 아니다.
 */

/** `YYYY-MM-DDTHH:mm` — `<input type="datetime-local">` 이 주고받는 꼴 */
const INPUT_SHAPE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/

/**
 * ⚠ `hourCycle: 'h23'` 이다. `hour12: false` 는 자정을 **`24`** 로 내놓는 구현이 있어
 *   하루가 밀린다(`Date.UTC(y, m, d, 24)` = 다음 날 0시).
 */
const PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: DISPLAY_TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})

interface Wall {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

/** 이 순간을 표시 시간대의 벽시계로 쪼갠다 */
function wallClockAt(utcMs: number): Wall {
  const parts = new Map<string, string>(
    PARTS.formatToParts(new Date(utcMs)).map((p) => [p.type, p.value]),
  )
  const read = (key: string): number => Number(parts.get(key) ?? '0')
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  }
}

/** 표시 시간대가 이 순간에 UTC 에서 얼마나 떨어져 있는가(ms) */
function offsetMsAt(utcMs: number): number {
  const wall = wallClockAt(utcMs)
  const asIfUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second)
  return asIfUtc - utcMs
}

const pad = (n: number): string => String(n).padStart(2, '0')

/**
 * 순간(ISO) → 입력칸 값. 표시 시간대의 벽시계다.
 * 값이 없거나 못 읽으면 **빈 문자열** — 지금 시각으로 대신 채우지 않는다(없는 사실을 그리지 않는다).
 */
export function toInputValue(iso: string | null | undefined): string {
  if (iso == null || iso === '') return ''
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return ''
  const wall = wallClockAt(ms)
  return `${String(wall.year)}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}`
}

/**
 * 입력칸 값 → 계약 값(UTC ISO, `Z`).
 *
 * 🔴 **`null` 은 「비었다」가 아니라 「못 읽었다」도 포함한다** — 호출부는 둘을 갈라 쓴다.
 *    빈 칸은 애초에 여기 넣지 않는다(선택 입력은 비면 키째 뺀다).
 * ⚠ 벽시계에는 offset 이 없어 **한 번 더 재서** 접는다. 표시 시간대에 DST 가 들어와도
 *   (지금은 `Asia/Seoul` 이라 없다) 경계 하루가 밀리지 않는다.
 */
export function toContractInstant(input: string): string | null {
  if (!INPUT_SHAPE.test(input)) return null
  const [datePart = '', timePart = ''] = input.split('T')
  const [year, month, day] = datePart.split('-').map(Number)
  const [hour, minute] = timePart.split(':').map(Number)
  if (year === undefined || month === undefined || day === undefined) return null
  if (hour === undefined || minute === undefined) return null

  const asIfUtc = Date.UTC(year, month - 1, day, hour, minute, 0)
  const firstGuess = asIfUtc - offsetMsAt(asIfUtc)
  const utcMs = asIfUtc - offsetMsAt(firstGuess)
  if (Number.isNaN(utcMs)) return null

  const instant = new Date(utcMs).toISOString()
  /*
   * 🔴 **왕복이 안 맞으면 버린다 — `Date.UTC` 가 범위 밖 값을 «조용히 굴리기» 때문이다.**
   *    `Date.UTC(2026, 12, 99, 99, 99)` 는 실패하지 않고 한참 뒤의 날짜가 된다. 꼴만 보는
   *    정규식은 `2026-13-99T99:99` 를 통과시키므로, 그대로 두면 **사용자가 적은 적 없는
   *    시각이 원장에 봉인된다.** 브라우저 `datetime-local` 이 그런 값을 잘 안 내놓는다는
   *    것은 근거가 못 된다 — 붙여넣기·자동완성·다른 WebView 가 있다(Tauri).
   * ⚠ 존재하지 않는 벽시계(DST 로 건너뛴 시각)도 여기서 걸린다. 그것도 버리는 게 맞다.
   */
  return toInputValue(instant) === input ? instant : null
}

/**
 * 이 입력이 미래인가. **보정은 하고 나서 기록한다** — 과거는 허용하고 미래만 막는다.
 * ⚠ 못 읽는 값은 `false` 다. 「미래다」라고 말할 근거가 없다 — 그건 별도 사유로 걸린다.
 */
export function isFutureInput(input: string, nowIso: string): boolean {
  const instant = toContractInstant(input)
  if (instant === null) return false
  const now = Date.parse(nowIso)
  return !Number.isNaN(now) && Date.parse(instant) > now
}

/**
 * 이 모듈에 **이미 기록된** 보정 종류. 입력칸의 제안으로만 쓴다.
 *
 * 🔴 **고를 목록의 «권위»가 아니다.** 어휘 원천은 모듈이 발행하는 `required_calibrations`
 *    (`module_service.openapi.yaml` — 「런타임 권위는 모듈이다」)인데 **오케스트레이터
 *    `ModuleSnapshot` 에 그 칸이 없다**(2026-09-21 실측). 계약도 `type` 을 자유 문자열로 두고
 *    DDL CHECK 를 일부러 걸지 않았다 — 정적 어휘가 아니기 때문이다.
 *    ⇒ **목록을 화면에 박지 않는다.** 처음 하는 보정은 이력이 없어 제안이 비고, 그때도
 *    **자유 입력을 막지 않는다.** 알람 `source` 에서 세운 판단과 같은 자리다.
 */
export function knownTypesOf(
  moduleId: string,
  calibrations: readonly { target?: string | null; type?: string | null }[],
): readonly string[] {
  const seen = new Set<string>()
  for (const record of calibrations) {
    if (record.target !== moduleId) continue
    if (record.type == null || record.type === '') continue
    seen.add(record.type)
  }
  return [...seen].sort((a, b) => a.localeCompare(b))
}
